<template>
  <div
    class="sf-bg-white sf-full-height tw-overflow-auto"
    data-testid="sf-insights-left-panel-lite"
  >
    <div :class="{ 'sf-layer-border-bottom': !inFloatingSidepane }">
      <div
        v-if="!inFloatingSidepane"
        class="tw-p-3 tw-flex tw-gap-4 tw-w-full sf-layer-border-bottom"
      >
        <SfButtonIcon icon="arrow-left" @click="closeLeftDrawer" />
      </div>

      <SegmentSelector />

      <div
        class="tw-px-4 tw-py-4 sf-below-2"
        :class="
          inFloatingSidepane
            ? 'tw-border-0 tw-border-b tw-border-solid tw-border-sf-slate-lighter-1'
            : 'sf-layer-border-bottom'
        "
      >
        <div
          class="sf-below-1"
          :class="
            inFloatingSidepane
              ? 'tw-text-sm tw-font-medium'
              : 'sf-title sf-font-l'
          "
        >
          {{ periodSelectionLabel }}
          <div class="sf-label sf-label-primary">
            {{ premiumLabel }}
          </div>
        </div>
        <template v-if="periodSelection">
          <div
            v-dompurify-html="
              I18n.t(
                'companies_overview.period_selection.short.' + periodSelection,
                { date: ledgerSelectDate || today }
              )
            "
            class="sf-pad-b-2"
          />
        </template>
        <div
          class="
            sf-btn sf-btn-primary sf-btn-inverted sf-alignc sf-full-width
            rs-add-trigger
            sf-level sf-level-around sf-c-primary
          "
          data-heap="premium-insight-period"
          @click="togglePremiumModal"
        >
          <div>{{ periodSelectionButton }}</div>
        </div>
      </div>
      <div class="tw-group">
        <FiltersSectionHeader
          class="tw-pl-4"
          :active-filters-count="activeFilters.length"
          :segment-loading="isSegmentLoading"
          @add="openAddFilterModal"
        />

        <div
          class="tw-p-4 tw-pt-0 tw-border-0 tw-border-b tw-border-solid"
          :class="
            inFloatingSidepane
              ? 'tw-border-sf-slate-lighter-1'
              : 'tw-border-sf-neutral-lighter-1'
          "
        >
          <div v-show="activeFilters.length">
            <ActiveFiltersList
              :edit-filter="togglePremiumModal"
              :immutable="true"
            ></ActiveFiltersList>
          </div>

          <div
            v-show="activeFilters.length == 0"
            class="
              tw-px-4
              sf-tab-item sf-c-text-darker sf-below-1 sf-hover
              tw-bg-sf-neutral-subtle-2
              hover:tw-bg-sf-neutral-subtle-2
            "
          >
            <div class="sf-level sf-level-between sf-pad-1">
              <div class="sf-level-item">
                <div>{{ clientsLabel }}</div>
                <div class="sf-font-light">{{ showAllLabel }}</div>
              </div>
            </div>
          </div>

          <SfButton
            data-testid="sf-insights-left-panel-add-filter-button"
            class="tw-w-full tw-mt-3"
            variant="info"
            @click="openAddFilterModal"
          >
            <span class="sf-icon fa fa-plus" />
            <div>
              {{ I18n.t('companies_overview.filters.add_filter_button') }}
            </div>
          </SfButton>
        </div>
      </div>

      <div
        class="tw-p-4"
        :class="
          inFloatingSidepane
            ? 'tw-border-0 tw-border-b tw-border-solid tw-border-sf-slate-lighter-1'
            : 'sf-layer-border-bottom'
        "
      >
        <div
          class="sf-below-1"
          :class="
            inFloatingSidepane
              ? 'tw-text-sm tw-font-medium'
              : 'sf-title sf-font-l'
          "
        >
          {{ communicationTriggersTitle }}
          <div class="sf-label sf-label-primary">
            {{ premiumLabel }}
          </div>
        </div>
        <div class="sf-font-1 sf-below-3 sf-c-text-darker">
          {{ communicationTriggersDesc }}
        </div>
        <a
          data-heap="premium-insight-trigger"
          class="
            sf-btn
            sf-btn-primary
            sf-btn-inverted
            sf-alignc
            sf-full-width
            sf-level
            sf-level-center
          "
          @click="togglePremiumModal"
          >{{ addCommunicationTriggerButton }}</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useCompaniesStore } from '../store/companies'
import { useFiltersStore } from '../store/filters'
import { useSegmentsStore } from '../store/segments'
import { useInsightsRootStore } from '../store/insights-root'

import ActiveFiltersList from './filters/active-filters-list'
import FiltersSectionHeader from './filters/filters-section-header.vue'
import SegmentSelector from './segments/segment-selector-lite'
import { INSIGHTS_LAYOUT_CONTEXT_KEY } from '../composables/use-insights-layout-context'
import { SfTime } from '@app/adapters/time'
import {
  SfButtonIcon,
  SfButton,
} from '@app/shared/vue/components/sf-button/index'

export default {
  components: {
    ActiveFiltersList,
    FiltersSectionHeader,
    SegmentSelector,
    SfButtonIcon,
    SfButton,
  },

  inject: {
    insightsLayoutContext: {
      from: INSIGHTS_LAYOUT_CONTEXT_KEY,
      default: () => ({ inFloatingSidepane: false }),
    },
  },

  computed: {
    inFloatingSidepane() {
      return this.insightsLayoutContext.inFloatingSidepane
    },
    ...mapPiniaState(useCompaniesStore, [
      'getError',
      'allCompaniesProcessed',
      'companies',
    ]),
    ...mapPiniaState(useSegmentsStore, ['isSegmentLoading']),
    ...mapPiniaState(useFiltersStore, ['allFilters', 'activeFilters']),

    ...mapPiniaState(useInsightsRootStore, [
      'periodSelection',
      'ledgerSelectDate',
    ]),
    ...mapPiniaState(useDrawer, ['isLeftDrawerOpen']),

    showEmptyCompanies() {
      return this.allCompaniesProcessed && !Object.keys(this.companies).length
    },

    manageTrigger() {
      return Silverfin.isCurrentUserAdmin()
    },

    clientsLabel() {
      return I18n.t('companies_overview_lite.clients')
    },

    showAllLabel() {
      return I18n.t('companies_overview_lite.show_all')
    },

    communicationTriggersTitle() {
      return I18n.t('companies_overview_lite.communication_triggers')
    },

    premiumLabel() {
      return I18n.t('companies_overview_lite.premium')
    },

    communicationTriggersDesc() {
      return I18n.t('companies_overview_lite.communication_triggers_desc')
    },

    addCommunicationTriggerButton() {
      return I18n.t('companies_overview_lite.add_communication_trigger')
    },

    allFiltersTitle() {
      return I18n.t('companies_overview_lite.all_filters')
    },

    allFiltersDesc() {
      return I18n.t('companies_overview_lite.all_filters_desc')
    },

    periodSelectionLabel() {
      return I18n.t('companies_overview.label_period_selection')
    },

    periodSelectionButton() {
      return I18n.t('companies_overview_lite.period_selection_button')
    },

    today: () =>
      I18n.t(
        'companies_overview.period_selection_new.date_selection_placeholder_with_date',
        { date: SfTime(new Date()).format('DD/MM/YYYY') }
      ),
  },

  methods: {
    ...mapPiniaActions(useInsightsRootStore, ['togglePremiumModal']),
    ...mapPiniaActions(useDrawer, ['closeLeftDrawer']),
    openAddFilterModal() {
      window.modalCitizen.openModalPortal('insights-add-filter-modal')
    },
  },
}
</script>
